import React, { useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { CurrentHolding } from '../hooks/useCurrentPortfolio';
import HoldingDetailsModal from './HoldingDetailsModal';
import Logo from '../../../shared/Logo';
import { getFlatPortfolioColors, portfolioTypography } from '../../../../utils/getFlatPortfolioColors';

interface HoldingCardProps {
  holding: CurrentHolding;
}

const HoldingCard: React.FC<HoldingCardProps> = ({ holding }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const theme = useTheme();
  const colors = getFlatPortfolioColors(theme);
  const currencySymbol = '$';
  const currencyCode = 'USD';

  const handleCardClick = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const formatCurrency = (amount: number) => {
    const locale = currencyCode === 'USD' ? 'en-US' : currencyCode === 'EUR' ? 'en-EU' : 'en-GB';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };


  const formatShares = (shares: number) => {
    return new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 6,
    }).format(shares);
  };

  const formatPercentage = (percent: number | undefined | null) => {
    // Handle undefined/null values gracefully
    if (percent === undefined || percent === null || isNaN(percent)) {
      return '0.00%';
    }
    const sign = percent >= 0 ? '+' : '';
    return `${sign}${percent.toFixed(2)}%`;
  };

  // Get functional color based on value (gains = green, losses = red)
  const getValueColor = (value: number | null | undefined) => {
    if (value === null || value === undefined) return colors.text.primary;
    return value >= 0 ? colors.functional.success : colors.functional.error;
  };

  return (
    <>
      <Box
        onClick={handleCardClick}
        sx={{
          backgroundColor: colors.background.secondary,
          border: `1px solid ${colors.border.primary}`,
          borderRadius: 0, // Sharp corners - no rounding
          padding: 3,
          cursor: 'pointer',
          transition: 'all 0.2s linear',
          '&:hover': {
            backgroundColor: colors.background.tertiary,
            borderColor: colors.border.strong,
          },
          // NO shadows, NO gradients, NO transforms - pure flat design
        }}
      >
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Logo
              src={holding.logo}
              alt={`${holding.name} logo`}
              ticker={holding.symbol}
              size={40}
            />
            <Box>
              <Typography sx={{ 
                ...portfolioTypography.body,
                fontWeight: 600,
                color: colors.text.primary,
                lineHeight: 1.2 
              }}>
                {holding.name}
              </Typography>
              <Typography sx={{ 
                ...portfolioTypography.caption,
                color: colors.text.muted 
              }}>
                {holding.ticker} • {formatShares(holding.shares)} shares
              </Typography>
            </Box>
          </Box>

          <Box textAlign="right">
            <Typography sx={{
              ...portfolioTypography.value,
              color: colors.text.primary,
              fontSize: '1.125rem',
            }}>
              {formatCurrency(holding.currentValue)}
            </Typography>
            <Typography sx={{
              ...portfolioTypography.caption,
              color: colors.text.muted,
            }}>
              {formatCurrency(holding.currentPrice)} per share
            </Typography>
          </Box>
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography sx={{ 
              ...portfolioTypography.label, 
              color: colors.text.muted,
              mb: 0.5 
            }}>
              TOTAL RETURN
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography sx={{
                ...portfolioTypography.value,
                fontSize: '1rem',
                color: getValueColor(holding.gainLoss),
              }}>
                {formatCurrency(holding.gainLoss)}
              </Typography>
              <Typography sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: getValueColor(holding.gainLoss),
                backgroundColor: holding.gainLoss >= 0 
                  ? colors.functional.successBg 
                  : colors.functional.errorBg,
                padding: '2px 6px',
              }}>
                {formatPercentage(holding.gainLossPercent)}
              </Typography>
            </Box>
          </Box>

          <Box textAlign="right">
            <Typography sx={{ 
              ...portfolioTypography.label, 
              color: colors.text.muted,
              mb: 0.5 
            }}>
              DAY CHANGE
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5} justifyContent="flex-end">
              <Typography sx={{
                fontSize: '0.75rem',
                color: getValueColor(holding.dayChange),
              }}>
                {holding.dayChange >= 0 ? '▲' : '▼'}
              </Typography>
              <Typography sx={{
                ...portfolioTypography.body,
                fontWeight: 600,
                color: getValueColor(holding.dayChange),
              }}>
                {formatCurrency(Math.abs(holding.dayChange))}
              </Typography>
              <Typography sx={{
                ...portfolioTypography.caption,
                color: getValueColor(holding.dayChange),
              }}>
                ({formatPercentage(holding.dayChangePercent)})
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            pt: 2,
            borderTop: `1px solid ${colors.border.secondary}`,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography sx={{ 
              ...portfolioTypography.caption, 
              color: colors.text.muted 
            }}>
              AVG COST: {formatCurrency(holding.averageCost)}
            </Typography>
            <Typography sx={{ 
              ...portfolioTypography.caption, 
              color: colors.text.muted 
            }}>
              COST BASIS: {formatCurrency(holding.totalCost)}
            </Typography>
          </Box>
        </Box>
      </Box>
    
    <HoldingDetailsModal
      open={modalOpen}
      onClose={handleModalClose}
      ticker={holding.ticker}
      companyName={holding.name}
      logo={holding.logo}
    />
    </>
  );
};

export default HoldingCard;